import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API, fmtMoney, extractErrorMessage } from "@/lib/api";
import { PayBadge } from "@/components/PayBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { payoutMethodError } from "@/lib/payoutValidation";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Printer, FileText, CreditCard, Star, Trash2, Building2 } from "lucide-react";

const METHOD_LABEL = { debit_card: "Debit card", zelle: "Zelle", bank_account: "Bank account (standard)" };

function PayoutSettings() {
  const [profile, setProfile] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [payeeName, setPayeeName] = useState("");
  const [method, setMethod] = useState("debit_card");
  const [accountRef, setAccountRef] = useState("");
  const [bankRouting, setBankRouting] = useState("");
  const [err, setErr] = useState("");

  const load = () => api.get("/server/payout-profile").then(({ data }) => setProfile(data)).catch(() => setProfile(null));
  useEffect(() => { load(); }, []);

  const methods = profile?.methods || [];
  const preferredId = profile?.preferred_method_id;

  const save = async () => {
    if (!payeeName.trim()) { setErr("Legal / payee name is required."); return; }
    const ve = payoutMethodError(method, accountRef, bankRouting);
    if (ve) { setErr(ve); return; }
    setErr("");
    setBusy(true);
    try {
      await api.post("/server/payout-profile", { payee_name: payeeName, method, account_ref: accountRef, routing_ref: bankRouting, make_preferred: methods.length === 0 });
      toast.success("Payout method saved"); setOpen(false); setAccountRef(""); setBankRouting(""); await load();
    } catch (e) { setErr(extractErrorMessage(e, "Could not save")); } finally { setBusy(false); }
  };
  const setPreferred = async (id) => { try { await api.post("/server/payout-profile/preferred", { method_id: id }); toast.success("Preferred method updated"); await load(); } catch { toast.error("Could not update"); } };
  const remove = async (id) => { try { await api.delete(`/server/payout-profile/methods/${id}`); toast.success("Method removed"); await load(); } catch { toast.error("Could not remove"); } };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="payout-settings">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 font-display text-base font-bold text-slate-900"><CreditCard className="h-4 w-4 text-emerald-600" /> Payout Settings</h3>
        <Button size="sm" variant="outline" data-testid="add-method-button" onClick={() => { setPayeeName(profile?.payee_name || ""); setErr(""); setOpen(true); }}>Add method</Button>
      </div>
      {methods.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="no-method-msg">No payout method on file. Add one to receive payouts.</p>
      ) : (
        <div className="space-y-2">
          {methods.map((m) => (
            <div key={m.id} data-testid={`method-row-${m.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-slate-800">{METHOD_LABEL[m.method] || m.method}</span>
                <span className="font-mono-arps text-slate-500">{m.masked_ref}</span>
                {m.id === preferredId && <span data-testid={`preferred-badge-${m.id}`} className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700"><Star className="h-3 w-3" /> Preferred</span>}
              </div>
              <div className="flex items-center gap-2">
                {m.id !== preferredId && <button data-testid={`set-preferred-${m.id}`} onClick={() => setPreferred(m.id)} className="text-xs font-semibold text-emerald-700 hover:underline">Set preferred</button>}
                <button data-testid={`remove-method-${m.id}`} onClick={() => remove(m.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add payout method</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Legal / payee name</Label><Input data-testid="settings-payee-input" value={payeeName} onChange={(e) => setPayeeName(e.target.value)} placeholder="John Q. Smith" /></div>
            <div>
              <Label>Payout method</Label>
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid="settings-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="debit_card">Debit card</SelectItem>
                  <SelectItem value="zelle">Zelle</SelectItem>
                  <SelectItem value="bank_account">Bank account (standard)</SelectItem>
                  <SelectItem value="paypal">PayPal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{method === "zelle" ? "Zelle email / phone" : method === "debit_card" ? "Debit card number" : method === "paypal" ? "PayPal recipient email" : "Bank account number"}</Label>
              <Input data-testid="settings-account-input" value={accountRef} onChange={(e) => setAccountRef(e.target.value)} placeholder={method === "zelle" ? "name@email.com" : method === "paypal" ? "recipient@email.com" : "•••• •••• •••• 1234"} />
              <p className="mt-1 text-xs text-slate-400">Only masked identifiers are stored. Full banking numbers are never saved or displayed.</p>
            </div>
            {method === "bank_account" && (
              <div>
                <Label>Routing number</Label>
                <Input data-testid="settings-routing-input" value={bankRouting} onChange={(e) => setBankRouting(e.target.value)} placeholder="9-digit routing number" />
                <p className="mt-1 text-xs text-slate-400">Required for bank-account payouts. Only a masked identifier is stored.</p>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-col">
            {err && <p className="text-sm font-medium text-red-600" data-testid="settings-method-error">{err}</p>}
            <Button data-testid="settings-save-button" onClick={save} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800">{busy ? "Saving…" : "Save method"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function periodOptions() {
  const out = [];
  const d = new Date();
  for (let i = 0; i < 6; i++) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const value = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
    out.push({ value, label: dt.toLocaleString(undefined, { month: "long", year: "numeric" }) });
  }
  return out;
}

function Line({ label, value, tone = "text-slate-700", testid }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span data-testid={testid} className={`font-semibold ${tone}`}>{value}</span>
    </div>
  );
}

function payoutStateLabel(c) {
  if (c.payment_status === "paid" || c.payout_state === "paid") return "Paid";
  if (c.payout_state === "processing") return "Processing";
  if (c.payout_state === "scheduled") return "Scheduled";
  if (c.payout_state === "failed") return "Failed — retry";
  return c.payout_eligible ? "Eligible" : "Awaiting payment";
}

function PayoutCard({ c, connect, onCashout, isAgencyProvider, isSolo }) {
  const nav = useNavigate();
  const cur = c.currency;
  const serverPrint = c.print_billable_to === "server" && (c.print_expense || 0) > 0;
  const [method, setMethod] = useState(connect?.payout_preference || "standard");
  const [busy, setBusy] = useState(false);
  const canCashout = c.payout_eligible && !["paid", "processing", "scheduled"].includes(c.payout_state) && c.payment_status !== "paid";
  const doCashout = async () => {
    setBusy(true);
    try { await onCashout(c.id, method); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`payout-card-${c.job_number}`} className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button data-testid={`payout-job-link-${c.job_number}`} onClick={() => c.order_id && nav(`/server/jobs/${c.order_id}`)}
            className="font-mono-arps text-sm font-semibold text-emerald-700 hover:underline">#{c.job_number}</button>
        </div>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600" data-testid={`payout-state-${c.job_number}`}>{payoutStateLabel(c)}</span>
      </div>
      <div className="divide-y divide-slate-100">
        <Line label="Service payout" value={fmtMoney(cur, c.service_payout)} testid={`payout-service-${c.job_number}`} />
        {(c.approved_expenses_total || 0) > 0 && (
          <Line label="Approved expenses" value={`+ ${fmtMoney(cur, c.approved_expenses_total)}`} tone="text-emerald-700" />
        )}
        {(c.adjustments || []).map((a, idx) => (
          <Line key={idx} label={`Adjustment — ${a.description}`}
                value={`${a.amount >= 0 ? "+ " : "- "}${fmtMoney(cur, Math.abs(a.amount))}`}
                tone={a.amount >= 0 ? "text-emerald-700" : "text-red-600"} />
        ))}
        <Line label="Gross payout" value={fmtMoney(cur, c.gross_payout)} />
        {serverPrint && (
          <div className="flex items-center justify-between py-1.5 text-sm">
            <span className="flex items-center gap-1.5 text-slate-500"><Printer className="h-3.5 w-3.5" /> Print expense ({c.print_pages || 0} pg)</span>
            <span data-testid={`payout-print-${c.job_number}`} className="font-semibold text-red-600">- {fmtMoney(cur, c.print_expense)}</span>
          </div>
        )}
        <Line label="Total deductions" value={`- ${fmtMoney(cur, c.deductions)}`} tone="text-red-600" />
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg bg-slate-900 px-4 py-3">
        <span className="text-sm font-medium text-slate-300">Net payout</span>
        <span data-testid={`payout-net-${c.job_number}`} className="font-display text-xl font-extrabold text-emerald-400">{fmtMoney(cur, c.net_payout)}</span>
      </div>
      {c.payment_status === "paid" && c.paid_at && (
        <p className="mt-2 text-right text-xs text-slate-400">Paid {new Date(c.paid_at).toLocaleDateString()}</p>
      )}
      {canCashout && (
        isAgencyProvider ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`agency-pays-note-${c.job_number}`}>
            <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-slate-400" />
            <p className="text-xs text-slate-600">
              {isSolo
                ? "Client payments settle directly to your own Stripe account — no cash-out needed."
                : "Your agency pays you directly."}
            </p>
          </div>
        ) : (
        <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 p-3" data-testid={`cashout-panel-${c.job_number}`}>
          {connect?.payouts_enabled ? (
            <div className="flex flex-wrap items-center gap-2">
              <Select value={method} onValueChange={setMethod}>
                <SelectTrigger data-testid={`cashout-method-${c.job_number}`} className="h-8 w-48 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard — free (weekly)</SelectItem>
                  <SelectItem value="instant">Instant — 5% fee (debit card)</SelectItem>
                </SelectContent>
              </Select>
              <Button size="sm" disabled={busy} data-testid={`cashout-btn-${c.job_number}`} onClick={doCashout} className="bg-emerald-600 text-white hover:bg-emerald-700">
                {busy ? "Processing…" : `Cash out ${fmtMoney(cur, method === "instant" ? c.net_payout * 0.95 : c.net_payout)}`}
              </Button>
            </div>
          ) : (
            <p className="text-xs text-amber-600" data-testid={`cashout-locked-${c.job_number}`}>Complete Stripe payout setup on the Onboarding page to cash out.</p>
          )}
        </div>
        )
      )}
    </div>
  );
}

export default function ServerPayouts() {
  const { user } = useAuth();
  const isAgencyProvider = !!(user && user.agency_id);
  const isSolo = !!(user && user.solo_subscriber);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connect, setConnect] = useState(null);

  const loadComps = () => api.get("/compensations").then(({ data }) => setComps(data)).finally(() => setLoading(false));
  const loadConnect = () => api.get("/server/connect/status").then(({ data }) => setConnect(data)).catch(() => setConnect(null));
  useEffect(() => { loadComps(); loadConnect(); }, []);

  const setPreference = async (pref) => {
    try { await api.post("/server/connect/payout-preference", { preference: pref }); setConnect((c) => ({ ...c, payout_preference: pref })); toast.success("Payout preference saved"); }
    catch { toast.error("Could not save preference"); }
  };
  const startConnect = async () => {
    try { const { data } = await api.post("/server/connect/onboard", {}); if (data.url) window.location.assign(data.url); }
    catch (e) { toast.error(extractErrorMessage(e, "Could not start payout setup")); }
  };
  const onCashout = async (compId, method) => {
    try {
      await api.post(`/compensations/${compId}/cashout`, { method });
      toast.success(method === "instant" ? "Instant payout initiated" : "Standard payout scheduled");
      await loadComps();
    } catch (e) { toast.error(extractErrorMessage(e, "Cash out failed")); }
  };

  const cur = comps[0]?.currency || "USD";
  const isPaid = (c) => c.payment_status === "paid" || c.payout_state === "paid";
  const isMoving = (c) => c.payout_state === "processing" || c.payout_state === "scheduled";
  const totalEarned = comps.reduce((s, c) => s + (c.net_payout || 0), 0);
  const processingTot = comps.filter(isMoving).reduce((s, c) => s + (c.net_payout || 0), 0);
  const paidTot = comps.filter(isPaid).reduce((s, c) => s + (c.net_payout || 0), 0);
  const awaiting = comps.filter((c) => !isPaid(c) && !isMoving(c)).reduce((s, c) => s + (c.net_payout || 0), 0);
  const [period, setPeriod] = useState(periodOptions()[0].value);

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Payouts</h2>
          <p className="text-slate-500">Compensation earned per completed service. Earned amounts are not "paid" until the transfer settles.</p>
        </div>
        <div className="flex items-end gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger data-testid="statement-period-select" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              {periodOptions().map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <a data-testid="download-statement-link" href={`${API}/compensations/statement.pdf?period=${period}`} target="_blank" rel="noreferrer">
            <Button variant="outline"><FileText className="mr-1.5 h-4 w-4" /> Download statement</Button>
          </a>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Completed Services</p>
          <p data-testid="payout-count" className="font-display text-3xl font-extrabold text-slate-900">{comps.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Total Earned</p>
          <p data-testid="payout-total-earned" className="font-display text-2xl font-extrabold text-slate-900">{fmtMoney(cur, totalEarned)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Awaiting Payment</p>
          <p data-testid="payout-awaiting" className="font-display text-2xl font-extrabold text-amber-600">{fmtMoney(cur, awaiting)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Processing</p>
          <p data-testid="payout-processing" className="font-display text-2xl font-extrabold text-sky-600">{fmtMoney(cur, processingTot)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5">
          <p className="text-sm text-slate-500">Paid</p>
          <p data-testid="payout-paid" className="font-display text-2xl font-extrabold text-emerald-600">{fmtMoney(cur, paidTot)}</p>
        </div>
      </div>

      <PayoutSettings />

      {/* Stripe Connect payout status */}
      {connect && !isAgencyProvider && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="connect-payout-status">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CreditCard className="h-4 w-4 text-indigo-600" />
              <h3 className="font-display text-base font-bold text-slate-900">Stripe payouts</h3>
              {connect.payouts_enabled
                ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid="connect-payouts-badge">Enabled</span>
                : <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid="connect-payouts-badge">Setup required</span>}
            </div>
            {!connect.payouts_enabled
              ? <Button size="sm" data-testid="connect-setup-payouts-btn" onClick={startConnect} className="bg-indigo-600 text-white hover:bg-indigo-700">{connect.has_account ? "Continue setup" : "Set up payouts"}</Button>
              : (
                <div className="flex items-center gap-2">
                  <Label className="text-xs text-slate-500">Default</Label>
                  <Select value={connect.payout_preference || "standard"} onValueChange={setPreference}>
                    <SelectTrigger data-testid="payout-preference-select" className="h-8 w-52 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard — free (weekly bank)</SelectItem>
                      <SelectItem value="instant">Instant — 5% fee (debit card)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            {connect.payouts_enabled
              ? (connect.instant_eligible ? "Instant payouts available on your eligible debit card. Not all cards/banks qualify." : "Standard weekly deposits enabled. Add an eligible debit card in Stripe for instant payouts — not all cards qualify.")
              : "Complete Stripe Connect onboarding to receive compensation. This is required to be eligible for assignments."}
          </p>
        </div>
      )}

      {loading ? <p className="text-slate-400">Loading…</p> : comps.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white px-5 py-14 text-center text-slate-400">
          <Wallet className="mx-auto mb-2 h-6 w-6 text-slate-300" />
          No payouts yet. A payout is created when your completed proof is approved.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {comps.map((c) => <PayoutCard key={c.id} c={c} connect={connect} onCashout={onCashout} isAgencyProvider={isAgencyProvider} isSolo={isSolo} />)}
        </div>
      )}
    </div>
  );
}
